import React from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { Question } from '../../../types';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';

const DEFAULT_MAILTO_RICH_TEXT = `
  <h3 style="margin:0 0 18px; font-size:25px; line-height:1.25; color:#2d3d4f; font-weight:700;">Why we need to talk to your prior auditor</h3>
  <p style="margin:0 0 12px; font-size:16px; line-height:1.5; color:#2d3d4f;">Stake how the language must specifically contain certain elements.</p>
  <p style="margin:0 0 12px; font-size:16px; line-height:1.5; color:#2d3d4f;">Use the email we can the copy to you.</p>
  <p style="margin:0; font-size:16px; line-height:1.5; color:#2d3d4f;">Clicking below will generate an email you can send to your prior auditor granting permission to discuss your past audit with CLA.</p>
`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface MailtoRendererProps {
  question: Question;
  selectedValue: Record<string, string | undefined>;
  onSelect: (value: Record<string, string | undefined>) => void;
}

export function buildMailtoLink(values: { email: string; cc?: string; auditorName?: string }): string {
  const params = new URLSearchParams();

  const email = values.email.trim();
  const cc = values.cc?.trim();
  if (cc) params.set('cc', cc);

  const auditorName = values.auditorName?.trim();
  if (auditorName) params.set('auditorName', auditorName);

  const raw = new URL(`mailto:${email || ''}`);
  raw.search = params.toString();
  return raw.toString();
}

function MailtoRenderer({ question, selectedValue, onSelect }: MailtoRendererProps): JSX.Element {
  const company = String(selectedValue?.company ?? '');
  const email = String(selectedValue?.email ?? '');
  const auditorName = String(selectedValue?.auditorName ?? '');
  const [blurredFields, setBlurredFields] = React.useState<Record<'company' | 'email', boolean>>({ company: false, email: false });
  const companyError = blurredFields.company && !company.trim() ? 'Auditing Firm is required' : '';
  const emailError = blurredFields.email
    ? (!email.trim() ? 'Auditor Email is required' : (!EMAIL_REGEX.test(email.trim()) ? 'Enter a valid email address' : ''))
    : '';

  const handleInputChange = (field: 'company' | 'email' | 'auditorName', nextValue: string): void => {
    const next = { company, email, auditorName, ...selectedValue, [field]: nextValue };
    onSelect({
      company: field === 'company' ? nextValue : next.company,
      email: field === 'email' ? nextValue : next.email,
      auditorName: field === 'auditorName' ? nextValue : next.auditorName,
    });
  };

  const richText = question.metadata?.description || DEFAULT_MAILTO_RICH_TEXT;
  const href = buildMailtoLink({ email, auditorName });
  const fieldLabelSx = { display: 'block', mb: 0.75, fontSize: '0.875rem', fontWeight: 500, color: '#1C3654' };
  const fieldInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#fff',
      minHeight: 48,
      '& fieldset': { borderColor: '#d0d9dc' },
      '&:hover fieldset': { borderColor: '#abc1c8' },
      '&.Mui-focused fieldset': { borderColor: '#3cc0d4', borderWidth: 1.5 },
    },
  };

  return (
    <Box sx={{ mt: 1, maxWidth: 1000 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.12fr 1fr' },
          gap: 2,
          alignItems: 'stretch',
          mb: 2,
        }}
      >
        <Box
          sx={{
            border: '1px solid #d7e0e5',
            borderTop: '4px solid #56c2ce',
            borderRadius: '8px',
            backgroundColor: '#f5f7f8',
            p: 3,
            minHeight: 350,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: 1,
              color: '#2d3d4f',
              '& h3': { m: 0, mb: 2, fontSize: '2rem', lineHeight: 1.1, fontWeight: 700, letterSpacing: '-0.02em' },
              '& p': { m: 0, mb: 1.25, fontSize: '1rem', lineHeight: 1.55, color: '#2d3d4f' },
              '& ul': { mt: 0.5, mb: 0, pl: 3 },
              '& li': { fontSize: '1rem', lineHeight: 1.5, color: '#2d3d4f', mb: 0.4 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(richText) }}
          />
        </Box>

        <Box
          sx={{
            border: '1px solid #d7e0e5',
            borderTop: '4px solid #1b2d4a',
            borderRadius: '8px',
            backgroundColor: '#f5f7f8',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            minHeight: 350,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.05rem',
              fontWeight: 700,
              color: '#2d3d4f',
              mb: 2,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}
          >
            Your Prior Auditor
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Typography component="label" htmlFor={`mailto-${question.id}-company`} className="cla-quiz-field-label" sx={fieldLabelSx}>Auditing Firm *</Typography>
              <TextField
                id={`mailto-${question.id}-company`}
                fullWidth
                value={company}
                onChange={(event) => handleInputChange('company', event.target.value)}
                onBlur={() => setBlurredFields((current) => ({ ...current, company: true }))}
                placeholder="Auditing Firm"
                required
                error={Boolean(companyError)}
                helperText={companyError || ' '}
                FormHelperTextProps={{ title: companyError || undefined, 'aria-hidden': !companyError || undefined, sx: { mt: '5px', minHeight: '1.25em', maxHeight: '1.25em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.25, visibility: companyError ? 'visible' : 'hidden' } }}
                sx={fieldInputSx}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor={`mailto-${question.id}-email`} className="cla-quiz-field-label" sx={fieldLabelSx}>Auditor Email *</Typography>
              <TextField
                id={`mailto-${question.id}-email`}
                fullWidth
                value={email}
                onChange={(event) => handleInputChange('email', event.target.value)}
                onBlur={() => setBlurredFields((current) => ({ ...current, email: true }))}
                placeholder="auditor@mycompany.com"
                required
                error={Boolean(emailError)}
                helperText={emailError || ' '}
                FormHelperTextProps={{ title: emailError || undefined, 'aria-hidden': !emailError || undefined, sx: { mt: '5px', minHeight: '1.25em', maxHeight: '1.25em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.25, visibility: emailError ? 'visible' : 'hidden' } }}
                sx={fieldInputSx}
              />
            </Box>

            <Box>
              <Typography component="label" htmlFor={`mailto-${question.id}-auditorName`} className="cla-quiz-field-label" sx={fieldLabelSx}>Auditor Name</Typography>
              <TextField
                id={`mailto-${question.id}-auditorName`}
                fullWidth
                value={auditorName}
                onChange={(event) => handleInputChange('auditorName', event.target.value)}
                placeholder="Auditor Name"
                sx={fieldInputSx}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default MailtoRenderer;
